// src/components/errors/ErrorSummaryCards.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { tokens } from '../../../theme';

export default function ErrorSummaryCards({
  summary,
  loading = false,
  timeRange = 24,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (loading || !summary) {
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="text" width="80%" height={20} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  const timeRangeLabel = timeRange === 1 ? 'hour' : timeRange < 24 ? 'hours' : timeRange === 24 ? 'day' : 'days';
  const timeRangeValue = timeRange >= 24 ? timeRange / 24 : timeRange;

  // Calculate most frequent error code
  const mostFrequentError = summary.errors_by_code 
    ? Object.entries(summary.errors_by_code).reduce((max, [code, count]) => 
        count > (max.count || 0) ? { code, count } : max, {})
    : {};

  // Calculate most problematic endpoint
  const mostProblematicEndpoint = summary.errors_by_endpoint
    ? Object.entries(summary.errors_by_endpoint).reduce((max, [endpoint, count]) => 
        count > (max.count || 0) ? { endpoint: endpoint.split(' ')[1] || endpoint, count } : max, {})
    : {};

  const cards = [
    {
      title: 'Total Errors',
      value: summary.total_errors?.toLocaleString() || '0',
      subtitle: `In the last ${timeRangeValue} ${timeRangeLabel}`,
      icon: <ErrorIcon sx={{ fontSize: 32 }} />,
      color: colors.redAccent[500],
      backgroundColor: colors.redAccent[900],
    },
    {
      title: 'Error Types',
      value: Object.keys(summary.errors_by_code || {}).length,
      subtitle: 'Unique error codes',
      icon: <WarningIcon sx={{ fontSize: 32 }} />,
      color: colors.yellowAccent[500],
      backgroundColor: colors.yellowAccent[900],
    },
    {
      title: 'Most Frequent',
      value: mostFrequentError.code || 'None',
      subtitle: `${mostFrequentError.count || 0} occurrences`,
      icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
      color: colors.blueAccent[500],
      backgroundColor: colors.blueAccent[900],
    },
    {
      title: 'Problem Endpoint',
      value: mostProblematicEndpoint.endpoint || 'None',
      subtitle: `${mostProblematicEndpoint.count || 0} errors`,
      icon: <ScheduleIcon sx={{ fontSize: 32 }} />,
      color: colors.pinkAccent[500],
      backgroundColor: colors.pinkAccent[900],
    },
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card 
            sx={{ 
              height: '100%',
              background: `linear-gradient(135deg, ${card.backgroundColor} 0%, ${theme.palette.background.paper} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: card.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
              
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                color={card.color}
                sx={{ mb: 0.5 }}
                noWrap
                title={card.value}
              >
                {card.value}
              </Typography>
              
              <Typography 
                variant="h6" 
                color="text.primary"
                sx={{ mb: 0.5, fontWeight: 500 }}
              >
                {card.title}
              </Typography>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: '0.875rem' }}
              >
                {card.subtitle}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}